import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, query, where, getDoc } from "firebase/firestore";
import { auth, db, storage } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import Layout from "../components/Layout";
import { exportToExcel } from "../utils/exportToExcel";

function SkuMaster() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [skus, setSkus] = useState([]);
  const [userProfile, setUserProfile] = useState(null);

  const [form, setForm] = useState({
    clientId: "",
    ItemCode: "",
    sku: "",
    Brand: "",
    Name: "",
    description: "",
    msku: "",
    segment: "",
    hsn: "",
    Basic_Eway: "",
    distributorBasicRate: "",
    dsBasicRate: "",
    gst: "",
    cess: "",
    addlcess: "",
    Rebate: "",
    openingstock: "",
    skutype: "",
    mrp: "",
    sortorder: "",
    swdorder: "",
    photoUrl: "",
    cartonsPerBag: "",
    packetsPerCarton: "",
    pcsPerPacket: "",
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [editingId, setEditingId] = useState(null);

  const portfolioMetrics = React.useMemo(() => {
    return {
      totalItems: skus.length,
      brands: [...new Set(skus.map(s => s.Brand))].length,
      segments: [...new Set(skus.map(s => s.segment))].length,
      avgMrp: skus.reduce((acc, curr) => acc + (Number(curr.mrp) || 0), 0) / (skus.length || 1)
    };
  }, [skus]);

  // Load SKUs
  const fetchSkus = async (profile) => {
    let q = collection(db, "skuMaster");
    if (profile && profile.role !== "superadmin") {
      q = query(collection(db, "skuMaster"), where("clientId", "==", profile.clientId));
    }
    const querySnapshot = await getDocs(q);
    const list = querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    setSkus(list);
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const snap = await getDoc(doc(db, "users", user.uid));
        if (snap.exists()) {
          const profile = snap.data();
          setUserProfile(profile);
          fetchSkus(profile);
        }
      }
    });
    return () => unsub();
  }, []);

  const updateForm = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const saveSku = async () => {
    try {
      let finalPhotoUrl = form.photoUrl;
      if (photoFile) {
        const fileRef = ref(storage, `products/${Date.now()}_${photoFile.name}`);
        const uploadRes = await uploadBytes(fileRef, photoFile);
        finalPhotoUrl = await getDownloadURL(uploadRes.ref);
      }
      const payload = { ...form, sku: (form.sku || "").trim().toUpperCase(), photoUrl: finalPhotoUrl };
      if (editingId) {
        await updateDoc(doc(db, "skuMaster", editingId), payload);
      } else {
        await addDoc(collection(db, "skuMaster"), payload);
      }
      setForm({
        clientId: "",
        ItemCode: "",
        sku: "",
        Brand: "",
        Name: "",
        description: "",
        msku: "",
        segment: "",
        hsn: "",
        Basic_Eway: "",
        distributorBasicRate: "",
        dsBasicRate: "",
        gst: "",
        cess: "",
        addlcess: "",
        Rebate: "",
        openingstock: "",
        skutype: "",
        mrp: "",
        sortorder: "",
        swdorder: "",
        photoUrl: "",
        cartonsPerBag: "",
        packetsPerCarton: "",
        pcsPerPacket: "",
      });
      setPhotoFile(null);
      setPhotoPreview("");
      setEditingId(null);
      fetchSkus(userProfile);
    } catch (err) {
      console.error("Error saving SKU:", err);
    }
  };

  const editSku = (sku) => {
    setEditingId(sku.id);
    const { id, ...rest } = sku;
    setForm(rest);
    setPhotoPreview(sku.photoUrl || "");
  };

  const removeSku = async (id) => {
    await deleteDoc(doc(db, "skuMaster", id));
    fetchSkus(userProfile);
  };

  const handleExportData = () => {
    if (skus.length === 0) {
      alert("No data to export.");
      return;
    }
    const exportData = skus.map(s => ({
      "Item Code": s.ItemCode || "",
      "SKU": s.sku || "",
      "Name": s.Name || "",
      "Brand": s.Brand || "",
      "Segment": s.segment || "",
      "HSN": s.hsn || "",
      "GST %": s.gst || 0,
      "CESS %": s.cess || 0,
      "MRP": s.mrp || 0,
      "Distributor Rate": s.distributorBasicRate || 0,
      "Salesman Rate": s.dsBasicRate || 0,
      "Retail Rate": s.Basic_Eway || 0,
      "Opening Stock": s.openingstock || 0,
      "CTN/Bag": s.cartonsPerBag || 0,
      "PKT/CTN": s.packetsPerCarton || 0,
      "PCS/PKT": s.pcsPerPacket || 0
    }));
    exportToExcel({
      data: exportData,
      fileName: `SKU_Master_Export_${new Date().toISOString().split('T')[0]}`,
      sheetName: "SKUs"
    });
  };

  return (
    <Layout>
      <div className="layout-content">
        <div style={{ marginBottom: 40, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
          <div>
            <h1 style={{ fontSize: 32, marginBottom: 8, fontWeight: 900, letterSpacing: "-0.02em" }}>SKU Portfolio Master</h1>
            <p style={{ color: "var(--text-muted)", fontSize: 16 }}>Curate your product catalog, manage multi-tier pricing, and regulate HSN compliance.</p>
          </div>
          <div style={{ display: "flex", gap: 30 }}>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: "var(--primary)" }}>{portfolioMetrics.totalItems}</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Total SKUs</div>
            </div>
            <div style={{ width: 1, height: 40, background: "var(--border)" }}></div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: "var(--text-main)" }}>{portfolioMetrics.brands}</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Brands Active</div>
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: userProfile?.role === "rsm" ? "1fr" : "1fr 340px", gap: 32 }}>
          {/* Main Content Area */}
          <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
            <div className="premium-card" style={{ padding: "16px 24px" }}>
              <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
                <div style={{ fontSize: 20 }}>🔍</div>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Query catalog by SKU, Brand, Segment or HSN code..."
                  style={{ flex: 1, border: "none", outline: "none", fontSize: 16, fontWeight: 500, background: "transparent" }}
                />
                <button onClick={handleExportData} className="btn-secondary" style={{ padding: "10px 20px", fontSize: 13, marginRight: 8 }}>📥 Export Excel</button>
                <button onClick={() => navigate("/sku-manager")} className="btn-secondary" style={{ padding: "10px 20px", fontSize: 13, display: userProfile?.role === "rsm" ? "none" : "block" }}>🚀 Bulk Protocol</button>
              </div>
            </div>

            <div className="premium-card" style={{ padding: 0, overflow: "hidden" }}>
              <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center", background: "var(--surface-hover)" }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Validated Product Records</h3>
                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--primary)" }}>SYNCED WITH FIREBASE CLOUD</div>
              </div>
              <div style={{ overflowX: "auto", maxHeight: 600 }}>
                <table className="enterprise-table">
                  <thead>
                    <tr>
                      <th style={{ width: 60 }}>Visual</th>
                      <th>Product Blueprint</th>
                      <th>HSN/GST Matrix</th>
                      <th>Tiered Pricing (INR)</th>
                      {userProfile?.role !== "rsm" && <th style={{ textAlign: "right" }}>Management</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {skus.filter(s => {
                      const term = search.toLowerCase();
                      return (
                        s.sku?.toLowerCase().includes(term) ||
                        s.Brand?.toLowerCase().includes(term) ||
                        s.Name?.toLowerCase().includes(term) ||
                        s.hsn?.toLowerCase().includes(term)
                      );
                    }).map((s) => (
                      <tr key={s.id}>
                        <td>
                          {s.photoUrl ? (
                            <img src={s.photoUrl} alt="sku" style={{ width: 44, height: 44, borderRadius: 8, objectFit: "contain", border: "1px solid var(--border)", padding: 2 }} />
                          ) : (
                            <div style={{ width: 44, height: 44, background: "var(--surface-hover)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "var(--text-muted)", fontWeight: 800 }}>NO IMG</div>
                          )}
                        </td>
                        <td>
                          <div style={{ fontWeight: 800, color: "var(--text-main)", fontSize: 15 }}>{s.Name}</div>
                          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{s.Brand} • {s.segment || "General"}</div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--primary)", marginTop: 4 }}>SKU-ID: {s.sku}</div>
                        </td>
                        <td>
                          <div style={{ fontWeight: 700, fontSize: 13 }}>{s.hsn || "---"}</div>
                          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>GST: {s.gst}% | CESS: {s.cess}%</div>
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: 12, fontSize: 12 }}>
                            <div>
                              <div style={{ fontWeight: 700 }}>₹{Number(s.distributorBasicRate || 0).toFixed(2)}</div>
                              <div style={{ fontSize: 9, color: "var(--text-muted)", textTransform: "uppercase" }}>Distributor</div>
                            </div>
                            <div style={{ width: 1, height: 20, background: "var(--border)" }}></div>
                            <div>
                              <div style={{ fontWeight: 700 }}>₹{Number(s.dsBasicRate || 0).toFixed(2)}</div>
                              <div style={{ fontSize: 9, color: "var(--text-muted)", textTransform: "uppercase" }}>Salesman</div>
                            </div>
                            <div style={{ width: 1, height: 20, background: "var(--border)" }}></div>
                            <div>
                              <div style={{ fontWeight: 700, color: "var(--success)" }}>₹{Number(s.Basic_Eway || 0).toFixed(2)}</div>
                              <div style={{ fontSize: 9, color: "var(--text-muted)", textTransform: "uppercase" }}>Retail</div>
                            </div>
                          </div>
                        </td>
                        {userProfile?.role !== "rsm" && (
                          <td style={{ textAlign: "right" }}>
                             <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                                <button onClick={() => editSku(s)} className="btn-secondary" style={{ padding: "6px 12px", fontSize: 11 }}>Update</button>
                                <button onClick={() => removeSku(s.id)} style={{ padding: "6px 12px", borderRadius: 8, border: "none", background: "rgba(239, 68, 68, 0.1)", color: "var(--error)", fontSize: 11, fontWeight: 800, cursor: "pointer" }}>Retire</button>
                             </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Right Sidebar Form */}
          {userProfile?.role !== "rsm" && (
            <div style={{ position: "sticky", top: 100 }}>
              <div className="premium-card" style={{ padding: 28 }}>
                <div style={{ marginBottom: 24 }}>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 900 }}>{editingId ? "Modify Protocol" : "New SKU Blueprint"}</h3>
                  <p style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 4 }}>Define technical specifications and price tiers.</p>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 800, color: "var(--text-muted)", marginBottom: 6, textTransform: "uppercase" }}>Identity Code</label>
                    <input type="text" className="form-input" name="sku" value={form.sku} onChange={updateForm} placeholder="e.g. SKU-123" style={{ width: "100%" }} />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 800, color: "var(--text-muted)", marginBottom: 6, textTransform: "uppercase" }}>Product Designation</label>
                    <input type="text" className="form-input" name="Name" value={form.Name} onChange={updateForm} placeholder="Full product title" style={{ width: "100%" }} />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div>
                      <label style={{ display: "block", fontSize: 11, fontWeight: 800, color: "var(--text-muted)", marginBottom: 6, textTransform: "uppercase" }}>Brand</label>
                      <input type="text" className="form-input" name="Brand" value={form.Brand} onChange={updateForm} placeholder="e.g. Supreme" style={{ width: "100%" }} />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 11, fontWeight: 800, color: "var(--text-muted)", marginBottom: 6, textTransform: "uppercase" }}>Segment</label>
                      <input type="text" className="form-input" name="segment" value={form.segment} onChange={updateForm} placeholder="e.g. Premium" style={{ width: "100%" }} />
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div>
                      <label style={{ display: "block", fontSize: 11, fontWeight: 800, color: "var(--text-muted)", marginBottom: 6, textTransform: "uppercase" }}>Dist. Rate</label>
                      <input type="number" className="form-input" name="distributorBasicRate" value={form.distributorBasicRate} onChange={updateForm} style={{ width: "100%" }} />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 11, fontWeight: 800, color: "var(--text-muted)", marginBottom: 6, textTransform: "uppercase" }}>DS Rate (Salesman)</label>
                      <input type="number" className="form-input" name="dsBasicRate" value={form.dsBasicRate} onChange={updateForm} style={{ width: "100%" }} />
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div>
                      <label style={{ display: "block", fontSize: 11, fontWeight: 800, color: "var(--text-muted)", marginBottom: 6, textTransform: "uppercase" }}>Retail Rate</label>
                      <input type="number" className="form-input" name="Basic_Eway" value={form.Basic_Eway} onChange={updateForm} style={{ width: "100%" }} />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 11, fontWeight: 800, color: "var(--text-muted)", marginBottom: 6, textTransform: "uppercase" }}>MRP</label>
                      <input type="number" className="form-input" name="mrp" value={form.mrp} onChange={updateForm} style={{ width: "100%" }} />
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div>
                      <label style={{ display: "block", fontSize: 11, fontWeight: 800, color: "var(--text-muted)", marginBottom: 6, textTransform: "uppercase" }}>HSN Code</label>
                      <input type="text" className="form-input" name="hsn" value={form.hsn} onChange={updateForm} style={{ width: "100%" }} />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 11, fontWeight: 800, color: "var(--text-muted)", marginBottom: 6, textTransform: "uppercase" }}>GST %</label>
                      <input type="number" className="form-input" name="gst" value={form.gst} onChange={updateForm} style={{ width: "100%" }} />
                    </div>
                  </div>
                  <div style={{ background: "var(--surface-hover)", padding: 16, borderRadius: 12, border: "1px solid var(--border)" }}>
                    <label style={{ display: "block", fontSize: 10, fontWeight: 900, color: "var(--primary)", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.1em" }}>Unit Configuration</label>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                      <div>
                        <label style={{ display: "block", fontSize: 9, fontWeight: 800, color: "var(--text-muted)", marginBottom: 4 }}>CTN / BAG</label>
                        <input type="number" className="form-input" name="cartonsPerBag" value={form.cartonsPerBag} onChange={updateForm} placeholder="10" style={{ width: "100%", fontSize: 12 }} />
                      </div>
                      <div>
                        <label style={{ display: "block", fontSize: 9, fontWeight: 800, color: "var(--text-muted)", marginBottom: 4 }}>PKT / CTN</label>
                        <input type="number" className="form-input" name="packetsPerCarton" value={form.packetsPerCarton} onChange={updateForm} placeholder="31" style={{ width: "100%", fontSize: 12 }} />
                      </div>
                      <div>
                        <label style={{ display: "block", fontSize: 9, fontWeight: 800, color: "var(--text-muted)", marginBottom: 4 }}>PCS / PKT</label>
                        <input type="number" className="form-input" name="pcsPerPacket" value={form.pcsPerPacket} onChange={updateForm} placeholder="35" style={{ width: "100%", fontSize: 12 }} />
                      </div>
                    </div>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 800, color: "var(--text-muted)", marginBottom: 6, textTransform: "uppercase" }}>Visual Signature</label>
                    <div style={{ display: "flex", gap: 12, alignItems: "center", padding: 12, borderRadius: 12, background: "var(--surface-hover)", border: "1px solid var(--border)" }}>
                      {photoPreview ? (
                        <img src={photoPreview} alt="prev" style={{ width: 40, height: 40, borderRadius: 6, objectFit: "contain", background: "white" }} />
                      ) : (
                        <div style={{ width: 40, height: 40, borderRadius: 6, background: "var(--border)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, color: "var(--text-muted)" }}>IMG</div>
                      )}
                      <input type="file" accept="image/*" onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setPhotoFile(file);
                          setPhotoPreview(URL.createObjectURL(file));
                        }
                      }} style={{ fontSize: 11, width: "100%" }} />
                    </div>
                  </div>
                  <button onClick={saveSku} className="btn-primary" style={{ width: "100%", padding: 16, marginTop: 12, fontSize: 15 }}>
                    {editingId ? "Commit Updates" : "Authorize New Record"}
                  </button>
                  {editingId && (
                    <button onClick={() => { setEditingId(null); setForm({ Name: "", sku: "" }); setPhotoPreview(""); }} className="btn-secondary">Abort Edit</button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default SkuMaster;
